<template>
    <div class="condition-dialog">
        <div
            v-if="selectingDataField"
            class="d-flex flex-column h-100">
            <pc-filtered-select
                id="selectableDataFields"
                ref="selectableDataFields"
                v-model="selectedDataFieldId"
                v-bind:label="dataFieldSearchListLabel"
                class="flex-grow-1"
                v-bind:items="dataFieldItems"
                v-bind:format-for-modal="true"
                value-prop="value"
                text-prop="text"
                v-on:update:model-value="onDataFieldSelected">
            </pc-filtered-select>
            <div class="d-flex mt-2">
                <pc-alert
                    v-bind:shown="showDataFieldError"
                    v-bind:message="dataFieldErrorMessage"
                    v-bind:severity="errorSeverity" />
            </div>
        </div>
        <div v-else>
            {{ selectedDataFieldNameLabel }}
            <div class="fw-bold">
                {{ selectedDataFieldDisplayName }}
            </div>
            <pc-button
                class="mt-2 mb-3"
                v-on:click="unselectDataField">
                {{ selectDataFieldButtonLabel }}
            </pc-button>
            <pc-select
                id="ConditionOperatorSelector"
                ref="ConditionOperatorSelector"
                v-model="conditionOperatorId"
                class="mt-2 mb-3"
                v-bind:label="conditionOperatorSelectorLabel"
                v-bind:options="conditionOperators"
                v-bind:validator="requiredValidator"
                text-prop="Description"
                value-prop="OperatorId">
                <option value="">
                    {{ genericNoSelectionOption }}
                </option>
            </pc-select>
            <div v-if="operandType === OPERAND_TYPE.String">
                <pc-input
                    id="OperandStringInput"
                    v-model="operandValue"
                    v-bind:alt-label="operandTextInputAltLabel" />
            </div>
            <div v-if="operandType === OPERAND_TYPE.Integer">
                <pc-input
                    id="OperandIntegerInput"
                    ref="OperandIntegerInput"
                    v-model="operandValue"
                    v-bind:validator="operandValidator"
                    v-bind:alt-label="operandTextInputAltLabel"
                    inputmode="numeric" />
            </div>
            <div v-if="operandType === OPERAND_TYPE.Decimal">
                <pc-input
                    id="OperandDecimalInput"
                    ref="OperandDecimalInput"
                    v-model="operandValue"
                    v-bind:validator="operandValidator"
                    v-bind:alt-label="operandTextInputAltLabel"
                    inputmode="decimal" />
            </div>
            <div v-if="operandType === OPERAND_TYPE.Boolean">
                <pc-radio-group
                    id="OperandBooleanSelector"
                    v-model="operandValue"
                    v-bind:alt-label="operandBooleanSelectorAltLabel"
                    v-bind:options="operandOptions"
                    name="OperandBooleanSelector"
                    text-prop="text"
                    value-prop="value" />
            </div>
            <div
                v-if="operandType === OPERAND_TYPE.Choice"
                id="OperandChoiceSelector">
                <pc-checkbox
                    v-for="option in operandOptions"
                    v-bind:key="option.value"
                    v-bind:label="option.text"
                    v-bind:model-value="option.checked"
                    v-bind:data-test-role="getTestRole(option.text)"
                    v-on:update:model-value="updateOperandSelections(option.value, $event)" />
            </div>
            <div v-if="operandType === OPERAND_TYPE.DateTime">
                <pc-date
                    id="OperandDatePicker"
                    v-model="operandValue"
                    v-bind:alt-label="operandDateInputAltLabel" />
            </div>
            <div
                v-if="operandErrorMessage"
                class="mt-2">
                <pc-alert
                    v-bind:shown="showOperandErrorMessage"
                    v-bind:severity="errorSeverity">
                    {{ operandErrorMessage }}
                </pc-alert>
            </div>
        </div>
    </div>
</template>

<script>
    import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
    import { RELATIONAL_OPERATOR_CONSTANT } from "Types/elementTypes";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { KEYS, NAMESPACE } from "./i18n/editEligibilityConditionDialog.i18n";
    import { mapI18n } from "Util/i18n";

    const OPERAND_TYPE = {
        None: 0,
        String: 1,
        Integer: 2,
        Decimal: 3,
        Boolean: 4,
        Choice: 5,
        DateTime: 6
    };

    export default {
        components: {},
        props: {
            condition: { type: Object, required: true },
            dataFields: { type: Array, required: true },
            operators: { type: Object, required: true },
            listCollection: { type: Object, required: true },
            isNew: { type: Boolean, required: true }
        },
        data() {
            return {
                OPERAND_TYPE: {},
                dataFieldItems: [],
                selectedDataField: null,
                showDataFieldError: false,
                conditionOperatorId: "",
                operandValue: "",
                operandOptions: [],
                operandValidator: getValidator(this.$app, VALIDATOR_TYPES.alwaysValid),
                requiredValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                operandErrorMessage: "",
                selectedDataFieldId: "" // Use this for v-model binding
            };
        },
        computed: {
            selectingDataField() {
                return !this.selectedDataField;
            },
            selectedDataFieldDisplayName() {
                if (this.selectedDataField) {
                    return this.selectedDataField.DisplayName;
                }
                return "";
            },
            selectedDataFieldType() {
                if (this.selectedDataField) {
                    return this.selectedDataField.DataFieldTypeId;
                }
                return 0;
            },
            conditionOperators() {
                switch (this.selectedDataFieldType) {
                    case DATA_FIELD_TYPE.String:
                        return this.operators.String;
                    case DATA_FIELD_TYPE.Integer:
                    case DATA_FIELD_TYPE.Decimal:
                        return this.operators.Numeric;
                    case DATA_FIELD_TYPE.Boolean:
                        return this.operators.Boolean;
                    case DATA_FIELD_TYPE.Choice:
                        return this.operators.Choice;
                    case DATA_FIELD_TYPE.DateTime:
                        return this.operators.Date;
                }
                return [];
            },
            conditionOperator() {
                if (this.conditionOperators && this.conditionOperatorId) {
                    return this.conditionOperators.find((o) => o.OperatorId === this.conditionOperatorId);
                }
                return null;
            },
            operandType() {
                if (
                    this.conditionOperatorId !== "" &&
                    this.conditionOperatorId !== RELATIONAL_OPERATOR_CONSTANT.IsEmpty &&
                    this.conditionOperatorId !== RELATIONAL_OPERATOR_CONSTANT.IsNotEmpty
                ) {
                    switch (this.selectedDataFieldType) {
                        case DATA_FIELD_TYPE.String:
                            return OPERAND_TYPE.String;
                        case DATA_FIELD_TYPE.Integer:
                            return OPERAND_TYPE.Integer;
                        case DATA_FIELD_TYPE.Decimal:
                            return OPERAND_TYPE.Decimal;
                        case DATA_FIELD_TYPE.Boolean:
                            return OPERAND_TYPE.Boolean;
                        case DATA_FIELD_TYPE.Choice:
                            return OPERAND_TYPE.Choice;
                        case DATA_FIELD_TYPE.DateTime:
                            return OPERAND_TYPE.DateTime;
                    }
                }
                return OPERAND_TYPE.None;
            },
            operandValueArray() {
                return this.operandValue.split(";");
            },
            showOperandErrorMessage() {
                return this.operandErrorMessage !== "";
            },
            errorSeverity() {
                return ALERT_SEVERITY.error;
            },
            // Localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            operandType() {
                this.setOperandOptions();
                this.setOperandValidator();
            },
            operandValue() {
                if (this.conditionOperatorId !== "") {
                    this.setOperandErrorMessage();
                }
            }
        },
        created() {
            this.OPERAND_TYPE = OPERAND_TYPE;
            // Format the data fields for the pc-filtered-select component
            this.dataFieldItems = this.dataFields.map((d) => ({
                text: d.DisplayName,
                value: d.PublicId
            }));
            if (this.condition) {
                this.selectDataFieldById(this.condition.DataFieldDefinitionId);
                if (this.selectedDataField) {
                    this.selectedDataFieldId = this.selectedDataField.PublicId;
                }
                this.conditionOperatorId = this.condition.OperatorId;
                if (this.operandType === OPERAND_TYPE.Boolean) {
                    switch (this.condition.RightOperand.toLowerCase()) {
                        case "1":
                        case "true":
                            this.operandValue = "true";
                            break;
                        case "0":
                        case "false":
                            this.operandValue = "false";
                            break;
                    }
                } else {
                    this.operandValue = this.condition.RightOperand;
                }
            }
        },
        methods: {
            getTestRole(text) {
                return text.replaceAll(" ", "-").toLowerCase();
            },
            selectDataFieldById(dataFieldId) {
                this.selectedDataField = this.dataFields.find((d) => d.PublicId === dataFieldId);
            },
            onDataFieldSelected(publicId) {
                if (publicId) {
                    this.selectDataFieldById(publicId);
                    this.selectedDataFieldId = publicId;
                    this.showDataFieldError = false;
                    this.resetCondition();
                } else {
                    this.showDataFieldError = true;
                }
            },
            unselectDataField() {
                this.selectedDataField = null;
                this.showDataFieldError = false;
                this.selectedDataFieldId = "";
            },
            resetCondition() {
                this.conditionOperatorId = "";
                this.operandValue = "";
                this.operandErrorMessage = "";
            },
            setOperandOptions() {
                let options = [];
                if (this.operandType === OPERAND_TYPE.Boolean) {
                    options = [
                        {
                            value: "true",
                            text: this.operandOptionTrueLabel,
                            checked: this.operandValue === "true"
                        },
                        {
                            value: "false",
                            text: this.operandOptionFalseLabel,
                            checked: this.operandValue === "false"
                        }
                    ];
                } else if (this.operandType === OPERAND_TYPE.Choice) {
                    const listId = this.selectedDataField.ListDefinitionId;
                    options = this.listCollection.ListItems.filter((i) => i.ListId === listId)
                        .filter((li) => !li.IsDeleted)
                        .map((li) => ({
                            value: li.ListItemId,
                            text: li.Name,
                            checked: this.operandOptionIsSelected(li.ListItemId.toString())
                        }))
                        .sort((a, b) => a.Order - b.Order);
                }
                this.operandOptions = options;
                this.operandErrorMessage = "";
            },
            setOperandValidator() {
                let dataValidatorType = VALIDATOR_TYPES.alwaysValid;
                switch (this.operandType) {
                    case OPERAND_TYPE.Integer:
                        dataValidatorType = VALIDATOR_TYPES.integer;
                        break;
                    case OPERAND_TYPE.Decimal:
                        dataValidatorType = VALIDATOR_TYPES.decimal;
                        break;
                }
                this.operandValidator = getValidator(this.$app, [dataValidatorType, VALIDATOR_TYPES.required]);
            },
            updateOperandSelections(selectedValue, isChecked) {
                const operand = this.operandOptions.find((o) => o.value === selectedValue);
                if (operand !== null) {
                    operand.checked = isChecked;
                }
                const selections = this.operandOptions.filter((o) => o.checked);
                this.operandValue = selections.map((s) => s.value).join(";");
            },
            operandOptionIsSelected(optionValue) {
                return this.operandValueArray.includes(optionValue);
            },
            setOperandErrorMessage() {
                this.operandErrorMessage = "";
                if (
                    this.operandType !== OPERAND_TYPE.None &&
                    this.operandType !== OPERAND_TYPE.Integer &&
                    this.operandType !== OPERAND_TYPE.Decimal
                ) {
                    if (this.operandValue === "") {
                        if (this.operandType === OPERAND_TYPE.Choice) {
                            this.operandErrorMessage = this.operandChoiceErrorMessage;
                        } else {
                            this.operandErrorMessage = this.operandValueErrorMessage;
                        }
                    }
                }
            },
            async verifyCanClose(result) {
                if (result === DIALOG_RESULT.cancel) {
                    return true;
                }
                if (this.selectingDataField) {
                    this.showDataFieldError = true;
                    return false;
                }
                this.$refs.ConditionOperatorSelector.validate();
                if (this.$refs.ConditionOperatorSelector.validationResult.hasError) {
                    return false;
                }
                if (this.operandType === OPERAND_TYPE.Integer) {
                    this.$refs.OperandIntegerInput.validate();
                    if (this.$refs.OperandIntegerInput.validationResult.hasError) {
                        return false;
                    }
                } else if (this.operandType === OPERAND_TYPE.Decimal) {
                    this.$refs.OperandDecimalInput.validate();
                    if (this.$refs.OperandDecimalInput.validationResult.hasError) {
                        return false;
                    }
                }
                this.setOperandErrorMessage();
                if (this.operandErrorMessage) {
                    return false;
                }
                return true;
            },
            resultHook() {
                if (this.selectedDataField) {
                    return {
                        NewDataField: this.selectedDataField.PublicId,
                        NewOperator: this.conditionOperatorId,
                        NewOperand: this.operandValue
                    };
                }
                return {};
            }
        }
    };
</script>

<style lang="scss">
    .condition-dialog {
        min-height: 256px;
    }

    .datafield-box {
        border-radius: var(--bs-border-radius);
        background-color: var(--bs-gray-500);
        width: 100%;
        color: black;
        font-weight: bold;
        text-align: left;

        &:hover {
            background-color: var(--bs-primary-200);
        }
    }
</style>
